import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { colors, radius } from '../theme';
import TiktokAnalytics from '../components/TiktokAnalytics';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = 'https://api.starhubcreative.id';

// Jarak waktu yang enak dibaca, dihitung dari fetched_at kiriman API — yaitu kapan
// angkanya benar-benar diambil dari YouTube, bukan kapan browser ini memanggil.
function labelWaktu(iso) {
  if (!iso) return '';
  const detik = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (detik < 60) return 'baru saja';
  const menit = Math.floor(detik / 60);
  if (menit < 60) return `${menit} menit lalu`;
  const jam = Math.floor(menit / 60);
  if (jam < 24) return `${jam} jam lalu`;
  return `${Math.floor(jam / 24)} hari lalu`;
}

// Periode (2026-10-12): rentang tanggal WIB untuk menyaring video menurut tanggal terbit.
const tglIni = (d = new Date()) => new Date(d.getTime() + 7 * 3600e3).toISOString().slice(0, 10);
const geser = (ymd, hari) => { const d = new Date(ymd + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + hari); return d.toISOString().slice(0, 10); };
function rentang(id, custom) {
  const hari = tglIni();
  const d = new Date(hari + 'T00:00:00Z');
  const senin = geser(hari, -((d.getUTCDay() + 6) % 7));
  const awalBulan = hari.slice(0, 8) + '01';
  switch (id) {
    case '7h': return { from: geser(hari, -6), to: hari };
    case '30h': return { from: geser(hari, -29), to: hari };
    case 'minggu': return { from: senin, to: hari };
    case 'mingguLalu': return { from: geser(senin, -7), to: geser(senin, -1) };
    case 'bulan': return { from: awalBulan, to: hari };
    case 'bulanLalu': return { from: geser(awalBulan, -1).slice(0, 8) + '01', to: geser(awalBulan, -1) };
    case 'kustom': return custom.from && custom.to ? (custom.from <= custom.to ? custom : { from: custom.to, to: custom.from }) : { from: null, to: null };
    default: return { from: null, to: null };
  }
}
const PERIODE_OPSI = [
  { id: 'semua', label: 'Semua waktu' }, { id: '7h', label: '7 hari terakhir' }, { id: '30h', label: '30 hari terakhir' },
  { id: 'minggu', label: 'Minggu ini' }, { id: 'mingguLalu', label: 'Minggu lalu' },
  { id: 'bulan', label: 'Bulan ini' }, { id: 'bulanLalu', label: 'Bulan lalu' }, { id: 'kustom', label: 'Pilih tanggal…' },
];
const fmtTgl = ymd => new Date(ymd + 'T00:00:00Z').toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const labelRentang = r => !r.from ? '' : r.from === r.to ? fmtTgl(r.from) : `${fmtTgl(r.from)} – ${fmtTgl(r.to)}`;

export default function Analytics() {
  const [data, setData] = useState({ channels: [], totals: { views: 0, likes: 0, comments: 0, videos: 0 }, daily_trend: [], best_hour: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedCh, setExpandedCh] = useState(null);
  const [ig, setIg] = useState({ accounts: [], totals: { follower_count: 0, reach: 0, likes: 0, comments: 0, views: 0, shares: 0, saves: 0 } });
  const [igLoading, setIgLoading] = useState(true);
  const [igError, setIgError] = useState('');
  const [fetchedAt, setFetchedAt] = useState(null);
  const [accounts, setAccounts] = useState(null); // jumlah akun terhubung per platform
  const [refreshing, setRefreshing] = useState(false);
  const [ttKey, setTtKey] = useState(0); // naik saat "Muat ulang" → analytics TikTok ikut diambil ulang
  const [, setTick] = useState(0);
  // Tampilan per jenis video (2026-10-09): semua | shorts (repost) | ugc. Disaring di server dari data
  // yang sama (cache), jadi ganti pilihan tidak memanggil YouTube lagi.
  const [jenis, setJenis] = useState('semua');
  const [gantiJenisLoading, setGantiJenisLoading] = useState(false);
  const jenisRef = useRef('semua');
  const [periode, setPeriode] = useState('semua');
  const [kustom, setKustom] = useState({ from: '', to: '' });
  const rentangRef = useRef({ from: null, to: null });
  const ytUrl = (paksa) => {
    const p = [];
    if (paksa) p.push('refresh=1');
    if (jenisRef.current !== 'semua') p.push('jenis=' + jenisRef.current);
    if (rentangRef.current.from) p.push('from=' + rentangRef.current.from, 'to=' + rentangRef.current.to);
    return `${API}/api/analytics/youtube${p.length ? '?' + p.join('&') : ''}`;
  };
  const aliveRef = useRef(true);

  // paksa=true menambah ?refresh=1 supaya API melewati cache-nya (1 menit).
  // Saat paksa, `loading` sengaja TIDAK dinyalakan: angka yang sudah tampil
  // dibiarkan di layar, cuma tombolnya yang berubah jadi "Memuat…".
  const muat = useCallback(async (paksa) => {
    const q = paksa ? '?refresh=1' : '';
    if (paksa) { setRefreshing(true); setTtKey(k => k + 1); }
    setError('');
    setIgError('');
    await Promise.all([
      axios.get(`${API}/api/analytics/accounts`).then(r => { if (aliveRef.current) setAccounts(r.data); }).catch(() => {}),
      (async () => {
        if (!paksa) setLoading(true);
        try {
          const res = await axios.get(ytUrl(paksa));
          if (aliveRef.current) { setData(res.data); setFetchedAt(res.data.fetched_at || null); }
        } catch (e) {
          if (aliveRef.current) setError(e.response?.data?.error || 'Gagal memuat analytics');
        }
        if (aliveRef.current && !paksa) setLoading(false);
      })(),
      (async () => {
        if (!paksa) setIgLoading(true);
        try {
          const res = await axios.get(`${API}/api/analytics/instagram${q}`);
          if (aliveRef.current) { setIg(res.data); if (res.data.error) setIgError(res.data.error); }
        } catch (e) {
          if (aliveRef.current) setIgError(e.response?.data?.error || 'Gagal memuat analytics Instagram');
        }
        if (aliveRef.current && !paksa) setIgLoading(false);
      })(),
    ]);
    if (aliveRef.current) setRefreshing(false);
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    muat(false);
    // Bikin label "diperbarui ..." ikut maju sendiri, tanpa request baru.
    const t = setInterval(() => { if (aliveRef.current) setTick(v => v + 1); }, 30000);
    return () => { aliveRef.current = false; clearInterval(t); };
  }, [muat]);

  const formatNum = (n) => Number(n || 0).toLocaleString('id-ID');

  // Ganti jenis/periode: ambil ulang dari cache server (tanpa panggilan YouTube baru).
  const muatSaring = async () => {
    const kunci = ytUrl(false);
    setExpandedCh(null);
    setGantiJenisLoading(true);
    try {
      const res = await axios.get(kunci);
      if (aliveRef.current && ytUrl(false) === kunci) { setData(res.data); setFetchedAt(res.data.fetched_at || null); }
    } catch (e) {
      if (aliveRef.current) setError(e.response?.data?.error || 'Gagal memuat analytics');
    }
    if (aliveRef.current) setGantiJenisLoading(false);
  };
  const gantiJenis = (j) => {
    if (j === jenisRef.current) return;
    jenisRef.current = j;
    setJenis(j);
    muatSaring();
  };
  const terapkanPeriode = (id, cust = kustom) => {
    setPeriode(id);
    const r = rentang(id, cust);
    if (id === 'kustom' && !r.from) return; // tunggu kedua tanggal diisi
    if (r.from === rentangRef.current.from && r.to === rentangRef.current.to) return;
    rentangRef.current = r;
    muatSaring();
  };
  const rAktif = rentangRef.current;
  const bs = data.by_source || {};
  const JENIS_OPSI = [
    { id: 'semua', label: 'Semua', n: null },
    { id: 'shorts', label: 'Shorts', n: bs.shorts?.videos },
    { id: 'ugc', label: 'UGC', n: bs.ugc?.videos },
  ];

  return (
    <div style={{ paddingBottom: 40 }}>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="ti ti-chart-bar" style={{ fontSize: 22 }}></i>Analytics
          </h1>
          <p style={{ fontSize: 13, color: colors.textSecondary, marginTop: 2 }}>
            Ringkasan performa video YouTube yang sudah diupload lewat sistem.
            {fetchedAt && (
              <> · <span title={new Date(fetchedAt).toLocaleString('id-ID')}>diperbarui {labelWaktu(fetchedAt)}</span></>
            )}
          </p>
        </div>
        <button
          onClick={() => muat(true)}
          disabled={loading || refreshing}
          title="Ambil ulang angka terbaru dari YouTube, tanpa menunggu cache"
          style={{
            display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
            padding: '8px 14px', fontSize: 12, fontWeight: 600,
            color: colors.textPrimary, background: colors.bgCard,
            border: `1px solid ${colors.border}`, borderRadius: radius.md,
            cursor: (loading || refreshing) ? 'default' : 'pointer',
            opacity: (loading || refreshing) ? 0.6 : 1,
          }}
        >
          <i className="ti ti-refresh" style={{ fontSize: 14 }}></i>
          {refreshing ? 'Memuat\u2026' : 'Muat ulang'}
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: colors.textSecondary }}>Memuat data analytics...</div>
      ) : error ? (
        <div style={{ padding: 16, background: colors.dangerMuted, color: colors.danger, borderRadius: radius.md }}>{error}</div>
      ) : (
        <>
          {accounts && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
              {[
                { label: 'Channel YouTube', icon: 'ti-brand-youtube', color: '#EF4444', v: accounts.youtube },
                { label: 'Akun TikTok', icon: 'ti-brand-tiktok', color: colors.textPrimary, v: accounts.tiktok },
                { label: 'Akun Instagram', icon: 'ti-brand-instagram', color: '#E1306C', v: accounts.instagram },
              ].map(a => (
                <div key={a.label} style={{ display: 'flex', alignItems: 'center', gap: 10, background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: '10px 14px' }}>
                  <i className={`ti ${a.icon}`} style={{ fontSize: 24, color: a.color }}></i>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: colors.textPrimary }}>{a.v ? a.v.connected : 0} <span style={{ fontSize: 11, fontWeight: 500, color: colors.textSecondary }}>terhubung</span></div>
                    <div style={{ fontSize: 11, color: a.v && a.v.needs_reconnect ? colors.danger : colors.textSecondary }}>
                      {a.label}{a.v && a.v.needs_reconnect ? ` · ${a.v.needs_reconnect} perlu reconnect` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Channel yang ditolak Google saat diambil: angkanya dari pengambilan terakhir yang berhasil
              (2026-10-08 — dulu dihitung 0 sehingga total turun-naik tiap refresh). */}
          {(data.stale_channels || []).length > 0 && (
            <div style={{ fontSize: 12, color: colors.textPrimary, background: colors.warningMuted, borderRadius: radius.md, padding: '8px 12px', marginBottom: 12, lineHeight: 1.5 }}>
              <b>{data.stale_channels.length} channel belum bisa diperbarui</b> dari YouTube saat ini, jadi memakai angka terakhir yang berhasil diambil:{' '}
              {data.stale_channels.map(c => `${c.channel_name}${c.stale_at ? ` (${labelWaktu(c.stale_at)})` : ' (belum pernah)'}`).join(', ')}. Klik "Muat ulang" beberapa saat lagi.
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, minWidth: 74 }}>Periode:</span>
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <i className="ti ti-calendar" style={{ position: 'absolute', left: 10, zIndex: 1, fontSize: 14, color: colors.textSecondary, pointerEvents: 'none' }}></i>
              <select value={periode} onChange={e => terapkanPeriode(e.target.value)} disabled={gantiJenisLoading} aria-label="Periode"
                style={{ appearance: 'none', WebkitAppearance: 'none', width: 'auto', margin: 0, padding: '7px 30px 7px 30px', fontSize: 12.5, fontWeight: 600, color: colors.textPrimary,
                  background: colors.bgCard, border: `1px solid ${periode !== 'semua' ? colors.accent : colors.border}`, borderRadius: radius.md, cursor: 'pointer' }}>
                {PERIODE_OPSI.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              <i className="ti ti-chevron-down" style={{ position: 'absolute', right: 10, zIndex: 1, fontSize: 13, color: colors.textSecondary, pointerEvents: 'none' }}></i>
            </div>
            {periode === 'kustom' && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}>
                {['from', 'to'].map((k, i) => (
                  <React.Fragment key={k}>
                    {i === 1 && <span style={{ fontSize: 12, color: colors.textSecondary }}>s/d</span>}
                    <input type="date" value={kustom[k]} max={tglIni()} aria-label={i ? 'Sampai tanggal' : 'Dari tanggal'}
                      onChange={e => { const c = { ...kustom, [k]: e.target.value }; setKustom(c); terapkanPeriode('kustom', c); }}
                      style={{ width: 138, margin: 0, padding: '6px 8px', fontSize: 12.5, color: colors.textPrimary, background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.md }} />
                  </React.Fragment>
                ))}
              </div>
            )}
            {rAktif.from && periode !== 'kustom' && <span style={{ fontSize: 12, color: colors.textSecondary }}>{labelRentang(rAktif)}</span>}
          </div>
          {rAktif.from && (
            <div style={{ fontSize: 11.5, color: colors.textSecondary, margin: '-2px 0 10px', lineHeight: 1.5 }}>
              <i className="ti ti-info-circle" style={{ fontSize: 13, marginRight: 4, verticalAlign: '-2px' }}></i>
              Menampilkan video yang <b>terbit</b> {labelRentang(rAktif)}. Angka views/likes = total video tersebut sampai hari ini.
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, minWidth: 74 }}>Jenis video:</span>
            <div style={{ display: 'inline-flex', background: colors.bgCardMuted, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: 3, gap: 3 }}>
              {JENIS_OPSI.map(o => (
                <button key={o.id} type="button" onClick={() => gantiJenis(o.id)} disabled={gantiJenisLoading}
                  style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: radius.sm, border: 'none', cursor: 'pointer',
                    background: jenis === o.id ? colors.bgCard : 'transparent', color: jenis === o.id ? colors.accentText : colors.textSecondary,
                    boxShadow: jenis === o.id ? '0 1px 2px rgba(0,0,0,0.08)' : 'none' }}>
                  {o.label}{o.n != null ? ` (${formatNum(o.n)})` : ''}
                </button>
              ))}
            </div>
            {gantiJenisLoading && <span style={{ fontSize: 12, color: colors.textSecondary }}>Memuat…</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20, opacity: gantiJenisLoading ? 0.5 : 1 }}>
            {[
              { label: 'Total Views', value: data.totals.views, icon: 'ti-eye', color: colors.accent },
              { label: 'Total Likes', value: data.totals.likes, icon: 'ti-thumb-up', color: colors.success },
              { label: 'Total Komentar', value: data.totals.comments, icon: 'ti-message-circle', color: colors.warning },
              { label: 'Total Video', value: data.totals.videos, icon: 'ti-video', color: colors.textSecondary, sub: jenis === 'semua' && data.totals.ugc_videos ? `termasuk ${data.totals.ugc_videos} UGC` : null },
            ].map(s => (
              <div key={s.label} style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: 16, textAlign: 'center' }}>
                <i className={`ti ${s.icon}`} style={{ fontSize: 22, color: s.color, marginBottom: 6, display: 'block' }}></i>
                <div style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary }}>{formatNum(s.value)}</div>
                <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>{s.label}</div>
                {s.sub && <div style={{ fontSize: 10, color: colors.textSecondary, marginTop: 2 }}>{s.sub}</div>}
              </div>
            ))}
          </div>

          {data.best_hour && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: colors.accentMuted, border: `1px solid ${colors.accent}`, borderRadius: radius.lg, marginBottom: 16 }}>
              <i className="ti ti-bulb" style={{ fontSize: 24, color: colors.accent, flexShrink: 0 }}></i>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: colors.accentText }}>Rekomendasi Jam Upload: {String(data.best_hour.hour).padStart(2, '0')}:00 WIB</div>
                <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>Berdasarkan {data.best_hour.video_count} video, rata-rata {data.best_hour.avg_views.toLocaleString('id-ID')} views di jam ini.</div>
              </div>
            </div>
          )}
          {data.daily_trend && data.daily_trend.length > 1 && (
            <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary, marginBottom: 12 }}>Views per Tanggal Terbit</div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data.daily_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: colors.textSecondary }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11, fill: colors.textSecondary }} />
                  <Tooltip contentStyle={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, fontSize: 12 }} />
                  <Line type="monotone" dataKey="views" stroke={colors.accent} strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: 4, marginBottom: 16 }}>
            <div style={{ padding: '14px 16px', fontSize: 13, fontWeight: 600, color: colors.textPrimary, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="ti ti-brand-instagram" style={{ fontSize: 15 }}></i>Performa Instagram
            </div>
            {igLoading ? (
              <div style={{ textAlign: 'center', padding: 30, color: colors.textSecondary, fontSize: 12 }}>Memuat data Instagram...</div>
            ) : igError ? (
              <div style={{ padding: 16, fontSize: 12, color: colors.textSecondary }}>{igError}</div>
            ) : ig.accounts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 30, color: colors.textSecondary, fontSize: 12 }}>
                Belum ada akun Instagram terhubung — hubungkan dulu di menu Hubungkan channel.
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8, padding: 16 }}>
                  {[
                    { label: 'Followers', value: ig.totals.follower_count, icon: 'ti-users' },
                    { label: 'Reach', value: ig.totals.reach, icon: 'ti-affiliate' },
                    { label: 'Likes', value: ig.totals.likes, icon: 'ti-heart' },
                    { label: 'Komentar', value: ig.totals.comments, icon: 'ti-message-circle' },
                    { label: 'Views', value: ig.totals.views, icon: 'ti-eye' },
                  ].map(s => (
                    <div key={s.label} style={{ background: colors.bgCardMuted, borderRadius: radius.md, padding: 10, textAlign: 'center' }}>
                      <i className={`ti ${s.icon}`} style={{ fontSize: 16, color: colors.textSecondary }}></i>
                      <div style={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary, marginTop: 2 }}>{formatNum(s.value)}</div>
                      <div style={{ fontSize: 10, color: colors.textSecondary }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                {ig.accounts.map(acc => (
                  <div key={acc.account_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderTop: `1px solid ${colors.border}`, flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: colors.textPrimary }}>@{acc.username || 'akun'}</div>
                    {acc.error ? (
                      <span style={{ fontSize: 11, color: colors.textSecondary }}>Gagal ambil data akun ini</span>
                    ) : (
                      <div style={{ display: 'flex', gap: 14, fontSize: 11, color: colors.textSecondary }}>
                        <span><i className="ti ti-affiliate" style={{ fontSize: 12, marginRight: 3 }}></i>{formatNum(acc.reach)}</span>
                        <span><i className="ti ti-heart" style={{ fontSize: 12, marginRight: 3 }}></i>{formatNum(acc.likes)}</span>
                        <span><i className="ti ti-message-circle" style={{ fontSize: 12, marginRight: 3 }}></i>{formatNum(acc.comments)}</span>
                        <span><i className="ti ti-eye" style={{ fontSize: 12, marginRight: 3 }}></i>{formatNum(acc.views)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>

          <TiktokAnalytics refreshKey={ttKey} />

          <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: 4 }}>
            <div style={{ padding: '14px 16px', fontSize: 13, fontWeight: 600, color: colors.textPrimary, borderBottom: `1px solid ${colors.border}` }}>
              Performa per Channel YouTube{jenis === 'ugc' ? ' · UGC' : jenis === 'shorts' ? ' · Shorts' : ''}{rAktif.from ? ` · ${labelRentang(rAktif)}` : ''}
            </div>
            {data.channels.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: colors.textSecondary }}>
                <i className="ti ti-chart-bar-off" style={{ fontSize: 32, marginBottom: 8, display: 'block' }}></i>
                {rAktif.from ? `Tidak ada video${jenis === 'ugc' ? ' UGC' : jenis === 'shorts' ? ' Shorts' : ''} yang terbit ${labelRentang(rAktif)}.`
                  : jenis === 'ugc' ? 'Belum ada video UGC yang bisa dianalisis.' : jenis === 'shorts' ? 'Belum ada video Shorts yang bisa dianalisis.' : 'Belum ada data video yang bisa dianalisis.'}
              </div>
            ) : data.channels.map(ch => (
              <div key={ch.channel_id}>
                <div onClick={() => setExpandedCh(expandedCh === ch.channel_id ? null : ch.channel_id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: `1px solid ${colors.border}`, flexWrap: 'wrap', gap: 8, cursor: 'pointer' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className={`ti ${expandedCh === ch.channel_id ? 'ti-chevron-down' : 'ti-chevron-right'}`} style={{ fontSize: 14, color: colors.textSecondary }}></i>
                    {ch.channel_name}
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: colors.textSecondary }}>
                    <span><i className="ti ti-eye" style={{ fontSize: 13, marginRight: 3 }}></i>{formatNum(ch.views)}</span>
                    <span><i className="ti ti-thumb-up" style={{ fontSize: 13, marginRight: 3 }}></i>{formatNum(ch.likes)}</span>
                    <span><i className="ti ti-message-circle" style={{ fontSize: 13, marginRight: 3 }}></i>{formatNum(ch.comments)}</span>
                    <span><i className="ti ti-video" style={{ fontSize: 13, marginRight: 3 }}></i>{ch.videos} video</span>
                  </div>
                </div>
                {expandedCh === ch.channel_id && (
                  <div style={{ padding: '8px 16px 16px', background: colors.bgCardMuted }}>
                    {(!ch.video_list || ch.video_list.length === 0) ? (
                      <div style={{ fontSize: 12, color: colors.textSecondary, textAlign: 'center', padding: 12 }}>Belum ada video.</div>
                    ) : ch.video_list.map(v => (
                      <a key={v.video_id} href={`https://youtu.be/${v.video_id}`} target="_blank" rel="noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${colors.border}`, textDecoration: 'none' }}>
                        {v.thumbnail ? (
                          <img src={v.thumbnail} alt="" style={{ width: 64, height: 36, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: 64, height: 36, borderRadius: 6, background: colors.border, flexShrink: 0 }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, color: colors.textPrimary, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {v.source === 'ugc' && <span style={{ fontSize: 10, fontWeight: 700, color: colors.accent, border: `1px solid ${colors.accent}`, borderRadius: 4, padding: '0 4px', marginRight: 6 }}>UGC</span>}
                            {v.title}
                          </div>
                          <div style={{ display: 'flex', gap: 10, fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                            <span><i className="ti ti-eye" style={{ fontSize: 11, marginRight: 2 }}></i>{formatNum(v.views)}</span>
                            <span><i className="ti ti-thumb-up" style={{ fontSize: 11, marginRight: 2 }}></i>{formatNum(v.likes)}</span>
                            <span><i className="ti ti-message-circle" style={{ fontSize: 11, marginRight: 2 }}></i>{formatNum(v.comments)}</span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
